import { ChangeDetectionStrategy, Component, ViewChild } from '@angular/core';
import {
	combineAll,
	DcBaseComponent,
	IListColumnConfig,
} from '@datachain/ui-sdk/common';
import { IBreadCrumbEntry } from '@datachain/ui-sdk/components/breadcrumb';
import { Store } from '@ngrx/store';
import { DxDataGridComponent } from 'devextreme-angular';
import { Map } from 'immutable';
import {
	BehaviorSubject,
	firstValueFrom,
	map,
	Observable,
	takeUntil,
	tap,
	timer,
} from 'rxjs';

import { goToGdHome, goToProjects } from '../../../core/store';
import { DcIcons } from '../../../ui/app-dc.icons';
import {
	fetchFormulaUsage,
	traceNavigateToReferenced,
} from '../../store/formula.actions';
import { FormulaSelector } from '../../store/formula.selector';
import { FormulaUsageEntity } from './formula-usage.entity';

const DX_DATA_GRID_TIMEOUT = 1000;

@Component({
	selector: 'app-formula-usages-list',
	templateUrl: './formula-usage-list.component.html',
	styleUrls: ['./formula-usage-list.component.scss'],
	inputs: ['projectLabel'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormulaUsageListComponent extends DcBaseComponent {
	public DcIcons = DcIcons;
	public DxDataGridTimeout = DX_DATA_GRID_TIMEOUT;
	public tranRefresh = $localize`:@@general.action.refresh:`;
	public tranSelectColumns = $localize`:@@general.action.columnSelector:`;
	public transExpandAll = $localize`:@@general.action.expandAll:`;
	public transCollapseAll = $localize`:@@general.action.collapseAll:`;

	public paths: Array<IBreadCrumbEntry> = [];

	public readonly allowedPageSizes = [5, 10, 100, 'all'];
	public readonly displayMode = 'full';

	@ViewChild(DxDataGridComponent, {
		static: false,
	})
	private readonly dataGrid!: DxDataGridComponent;

	public vo$: Observable<{
		isFirstLoad: boolean;
		isLoading: boolean;
		isAllExpanded: boolean;
		formulas: Map<string, FormulaUsageEntity>;
		availableColumns: Map<string, IListColumnConfig<FormulaUsageEntity>>;
		usageCount: number;
	}>;

	private readonly isAllExpandedSubject = new BehaviorSubject<boolean>(false);

	private readonly firstLoadSubject = new BehaviorSubject<boolean>(true);

	public constructor(
		private readonly store: Store,
		private readonly formulaSelector: FormulaSelector
	) {
		super();
		this.cmpId = 'formula-usage';
		this.dxLocalStorageKey = 'dx.grid.formula:usage';

		this.store.dispatch(fetchFormulaUsage());

		combineAll({
			projectLabel: this.toObservable<string>('projectLabel'),
		})
			.pipe(
				takeUntil(this.onDestroy$),
				tap(({ projectLabel }) => {
					this.paths = [
						{
							label: $localize`:i18n=@@general.projects:`,
							isActive: true,
							callback: (): void => this.store.dispatch(goToProjects()),
						},
						{
							label: projectLabel,
							isActive: true,
							callback: (): void => this.store.dispatch(goToGdHome()),
						},
						{
							label: $localize`:i18n=@@formulas.usages:`,
							isActive: false,
							callback: (): void => {},
						},
					];
				})
			)
			.subscribe();

		this.vo$ = combineAll({
			isFirstLoad: this.firstLoadSubject,
			isLoading: this.formulaSelector.getLoading$(),
			list: this.formulaSelector.getFormulaUsages$(),
			usageCount: this.formulaSelector.getUsageCount$(),
			isAllExpanded: this.isAllExpandedSubject,
		}).pipe(
			map(({ isFirstLoad, isLoading, list, usageCount, isAllExpanded }) => ({
				isFirstLoad,
				isLoading,
				isAllExpanded,
				usageCount,
				formulas: list.elements,
				availableColumns: list.getAvailableColumns(),
			}))
		);
	}

	public navigateTo(url: string, referenced = 'link'): void {
		this.store.dispatch(
			traceNavigateToReferenced({
				url,
				referenced,
			})
		);
	}

	public refresh(): void {
		this.firstLoadSubject.next(true);
		this.dataGrid?.instance.beginCustomLoading('');
		this.store.dispatch(fetchFormulaUsage());
	}

	public async toggle(): Promise<void> {
		const current = await firstValueFrom(this.isAllExpandedSubject);
		this.isAllExpandedSubject.next(!current);
		return current
			? this.dataGrid.instance.collapseAll(-1)
			: this.dataGrid.instance.expandAll(-1);
	}

	public async onGridContentReady(): Promise<void> {
		const firstLoad = await firstValueFrom(this.firstLoadSubject);

		if (firstLoad) {
			await firstValueFrom(
				timer(1000).pipe(
					tap(() => {
						this.firstLoadSubject.next(false);
						this.dataGrid?.instance.endCustomLoading();
					})
				)
			);
		}
	}
}
